import { Sun, Moon, Search, ChevronRight, Building2, LogOut, Settings, Plug, HelpCircle } from 'lucide-react';
import { NotificationBell } from './NotificationBell';
import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import { motion } from 'motion/react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/primitives';
import { useTheme } from '@/lib/theme';
import { BusinessUnitSelector } from './BusinessUnitSelector';
import { useEntitlements } from '@/lib/use-entitlements';
import { clearAuth, getAuthUser } from '@/lib/api';
import { friendlyName } from '@/lib/display-name';

/**
 * Derive a 2-letter avatar initials from a displayName or email.
 * "Aanya Sharma" → "AS"; "alice@example.com" → "AL".
 */
function deriveInitials(name: string | null | undefined, email: string | null | undefined): string {
  if (name) {
    const parts = name.trim().split(/\s+/).slice(0, 2);
    const letters = parts.map((p) => p[0]?.toUpperCase() ?? '').join('');
    if (letters.length >= 1) return letters.padEnd(2, letters[0] ?? 'U');
  }
  if (email) return email.slice(0, 2).toUpperCase();
  return 'U';
}

/** Render the tenant tier label per the 8-bundle catalogue (BFSI, Healthcare, etc.). */
function formatTier(tier: string | null | undefined, industry: string | null | undefined): string {
  const tierLabel = tier ? tier.charAt(0).toUpperCase() + tier.slice(1).replace(/_/g, ' ') : null;
  const indLabel = industry
    ? industry.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
    : null;
  if (tierLabel && indLabel) return `${tierLabel} · ${indLabel}`;
  if (tierLabel) return tierLabel;
  if (indLabel) return indLabel;
  return '';
}

export function TopBar({ onOpenPalette }: { onOpenPalette: () => void }) {
  const { theme, toggleTheme } = useTheme();
  const router = useRouterState();
  const navigate = useNavigate();
  const segments = router.location.pathname.split('/').filter(Boolean);

  // Phase 0 item 1 (2026-05-26) - bind tenant identity to entitlements.
  // Phase 0 item 8 - bind user identity to auth store (was hardcoded
  // "DA · Demo Actor · DPO · acme-bank.com").
  const { entitlements } = useEntitlements();
  const user = getAuthUser();
  const tenantName = entitlements?.tenant?.displayName ?? entitlements?.tenant?.slug ?? '-';
  const tenantSubtitle = formatTier(
    entitlements?.tenant?.tier,
    entitlements?.tenant?.industry,
  );
  // displayName from the bridge is the email; present a friendly name in
  // the chrome and keep the email on the sub-line (see lib/display-name).
  const friendly = friendlyName(user?.displayName, user?.email);
  const userName = friendly.full ?? user?.email ?? 'User';
  const userRole = user?.role ?? '';
  const userEmail = user?.email ?? '';
  const initials = deriveInitials(friendly.full, user?.email);

  // Phase 0 item 2 (2026-05-26) - Sign Out handler. Clears the local
  // auth store (token + user + expiry) and redirects to /login.
  // Mode A (standalone): hits Discovery's own /login page.
  // Mode B (Core-paired): /login renders the bridge-aware page that
  //   re-redirects to Core if the host is a Core-managed apex.
  // Mode C (bundle): same as B.
  function handleSignOut(): void {
    clearAuth();
    void navigate({ to: '/login' });
  }

  return (
    <header className="glass sticky top-0 z-40 flex h-14 items-center gap-3 px-4 border-b border-border/40">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-1.5 text-xs text-muted-foreground min-w-0">
        <Building2 className="h-3.5 w-3.5 shrink-0" />
        {segments.length === 0 && <span className="text-foreground font-medium">Home</span>}
        {segments.map((seg, i) => (
          <span key={i} className="flex items-center gap-1.5">
            <ChevronRight className="h-3 w-3 opacity-50" />
            <span className={i === segments.length - 1 ? 'text-foreground font-medium capitalize' : 'capitalize'}>
              {seg.replace(/-/g, ' ')}
            </span>
          </span>
        ))}
      </nav>

      <div className="flex-1" />

      {/* Cmd+K palette trigger */}
      <button
        onClick={onOpenPalette}
        className="glass-lite group flex items-center gap-2 rounded-md px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors w-72"
      >
        <Search className="h-3.5 w-3.5" />
        <span className="flex-1 text-left">Search modules, findings, decisions…</span>
        <kbd className="font-mono text-[10px] opacity-60 group-hover:opacity-100 transition-opacity">⌘K</kbd>
      </button>

      {/* BU selector - shared with Core via localStorage `vaultconsent-ui`.
          Hides itself when the tenant has 0 BUs; static label for 1 BU;
          dropdown for 2+. See stores/ui.ts for the cross-app sync. */}
      <BusinessUnitSelector />

      <div className="flex items-center gap-1">
        {/* Wave 1 (2026-05-27) - real Notifications bell wired to
            /v1/notifications/* (SSE-streamed). Replaces the prior
            placeholder bell that displayed a hardcoded "3 critical"
            badge against no real data. */}
        <NotificationBell />

        <TooltipProvider delayDuration={300}>
          {/* OBS-17 (2026-05-26) - Help shortcut. The /help page existed
              all along but was unreachable from the chrome; the only
              way in was a deep link or via the demo tour. Operators
              asking "where do I get help" had nowhere to click. */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon" asChild>
                <Link to="/help" aria-label="Open help center">
                  <HelpCircle className="h-4 w-4" />
                </Link>
              </Button>
            </TooltipTrigger>
            <TooltipContent>Help &amp; keyboard shortcuts</TooltipContent>
          </Tooltip>

          {/* Theme toggle - uses View Transitions API for cross-fade morph */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon" onClick={toggleTheme}>
                <motion.span
                  key={theme}
                  initial={{ rotate: -90, scale: 0.5, opacity: 0 }}
                  animate={{ rotate: 0, scale: 1, opacity: 1 }}
                  transition={{ duration: 0.25, ease: 'easeOut' }}
                  className="inline-block"
                >
                  {theme === 'dark' ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
                </motion.span>
              </Button>
            </TooltipTrigger>
            <TooltipContent>Switch to {theme === 'dark' ? 'light' : 'dark'} mode</TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {/* Tenant pill + user menu */}
        <div className="flex items-center gap-2 ml-2 pl-2 border-l border-border/40">
          {/* OBS-18 (2026-05-26) - tenant name is now clickable;
              navigates to the tenant overview at /admin/compliance-posture
              (the closest "tenant home" view in the current IA). */}
          <Link
            to="/admin/compliance-posture"
            className="text-right text-xs hidden md:block hover:opacity-80 transition-opacity"
            aria-label="Tenant settings"
          >
            <div className="font-medium leading-tight">{tenantName}</div>
            {tenantSubtitle && (
              <div className="text-muted-foreground text-[10px]">{tenantSubtitle}</div>
            )}
          </Link>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="rounded-full">
                <span className="h-7 w-7 rounded-full bg-gradient-to-br from-chart-1 to-chart-7 flex items-center justify-center text-white text-xs font-semibold">
                  {initials}
                </span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <div className="px-2 py-1.5 text-xs">
                <div className="font-medium">{userName}</div>
                {(userRole || userEmail) && (
                  <div className="text-muted-foreground">
                    {userRole && <span>{userRole}</span>}
                    {userRole && userEmail && <span> · </span>}
                    {userEmail && <span>{userEmail}</span>}
                  </div>
                )}
              </div>
              <DropdownMenuSeparator />
              {/*
                Phase 0 item 3 (2026-05-26) - Profile + Switch Tenant
                menu items HIDDEN until their respective routes exist.
                  - Profile page: not yet built; will land in Phase 2 alongside
                    the user-self-service surface.
                  - Switch Tenant: per-user multi-tenant membership flow not
                    yet built; the bridge-token redirect from Core is the
                    current cross-tenant story.
                Honest UX: don't show a control with no destination.
              */}
              <DropdownMenuItem asChild>
                <Link to="/admin/integrations" className="cursor-pointer">
                  <Plug /> Integrations
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/admin/integrations/foctta-core" className="cursor-pointer">
                  <Settings /> Settings
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-destructive focus:text-destructive cursor-pointer"
                onSelect={handleSignOut}
              >
                <LogOut /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
